import { DOCUMENT, DestroyRef, Injectable, Injector, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
    DslEnvService,
    DslService,
    EventContext,
    EventType,
    EventsService,
    Logger,
    NativeAppService,
    NativeEvent,
    NativeEventType,
    ProductService,
    RtmsMessage,
    RtmsService,
    SimpleEvent,
    UserEvent,
    UserLogoutEvent,
    UserPreHooksLoginEvent,
    UserService,
    VanillaEventNames,
    WebWorkerService,
    WindowEvent,
} from '@frontend/vanilla/core';
import { PLATFORM, linkedTransferState } from '@frontend/vanilla/ssr';
import { Subject, debounceTime, filter, map, take } from 'rxjs';

import { LoaderStylesService } from './loader-styles.service';
import { LoadOptions, LoadersConfig, StyleOptions } from './loaders.client-config';
import { LOADER_FN_TOKEN, LoadState, LoadStrategy, LoaderFnConfig, LoaderLoadOptions } from './loaders.models';
import { ModuleService } from './module.service';

@Injectable({ providedIn: 'root' })
export class LoaderService {
    private registeredLoaderFns = inject(LOADER_FN_TOKEN);
    private config = inject(LoadersConfig);
    private dslService = inject(DslService);
    private dslEnvService = inject(DslEnvService);
    private user = inject(UserService);
    private webWorkerService = inject(WebWorkerService);
    private nativeAppService = inject(NativeAppService);
    private logger = inject(Logger);
    private rtmsService = inject(RtmsService);
    private eventsService = inject(EventsService);
    private moduleService = inject(ModuleService);
    private loadStylesService = inject(LoaderStylesService);
    private productService = inject(ProductService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly _doc = inject(DOCUMENT);
    private readonly platform = inject(PLATFORM);

    private readonly loadDispatcher$ = new Subject<LoaderLoadOptions>();

    private readonly STYLES_LOADER_ID = 'styles';
    private readonly allLoaders: LoaderLoadOptions[] = [];

    // Track which loaders are used on the server for preloading on the browser
    private readonly _usedLoaders = linkedTransferState<string[]>('usedLoaders', []);

    init() {
        this.initLoaders();

        this.loadDispatcher$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((options: LoaderLoadOptions) => this.load(options));

        // start just logged-in strategy when user logs in (only once)
        this.user.events
            .pipe(
                filter((e: UserEvent) => e instanceof UserPreHooksLoginEvent),
                take(1),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                this.start(LoadStrategy.JustLoggedIn);
                this.start(LoadStrategy.LoggedIn);
            });
        this.user.events
            .pipe(
                filter((e: UserEvent) => e instanceof UserLogoutEvent),
                take(1),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                this.reset([LoadStrategy.JustLoggedIn, LoadStrategy.LoggedIn]);
            });

        // re-evaluate DSL conditions on any DSL or Product change
        this.dslEnvService.change.pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.restart();
        });

        this.productService.productChanged.subscribe(() => {
            this.restart();
        });

        // Vanilla events
        this.eventsService.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((e: SimpleEvent) => {
            const event: EventContext<any> = { name: e.eventName, type: EventType.Vanilla, data: e.data };
            this.startEventStrategy(event);
        });

        // RTMS events
        this.rtmsService.messages.subscribe((e: RtmsMessage) => {
            const event: EventContext<any> = { name: e.type, type: EventType.Rtms, data: e.payload };
            this.startEventStrategy(event);
        });

        // Native events
        this.nativeAppService.eventsFromNative.subscribe((e: NativeEvent) => {
            const event: EventContext<any> = { name: e.eventName, type: EventType.Native, data: e.parameters };
            this.startEventStrategy(event);
        });

        // preload always first
        this.start(LoadStrategy.Preload);

        // then loggedin, if authenticated
        if (this.user.isAuthenticated) {
            this.start(LoadStrategy.AlreadyLoggedIn);
            this.start(LoadStrategy.LoggedIn);
        }

        // then complete
        this._doc.addEventListener(WindowEvent.ReadyStateChange, () => this.runOnlyCompleteStrategy());
        this.runOnlyCompleteStrategy();
    }

    async loadFeature(featureId: string, featureImport: Promise<any>, options: LoadOptions, additionalStyles?: StyleOptions[]) {
        const styles = this.mergeStyles(options.styles, additionalStyles);

        await Promise.all([
            featureImport.then((feature: any) => {
                const featureInjector = Injector.create({
                    providers: feature.provide(),
                    parent: this.getInjector(),
                });

                this.moduleService.runBootstrappers(featureInjector);
                this.moduleService.runDslProviders(featureInjector);
                this.moduleService.runLaunchDarklyContextProvider(featureInjector);

                if (options.eventContext) {
                    this.moduleService.runEventProcessors(featureInjector, options.eventContext);
                }

                this.nativeAppService.sendToNative({
                    eventName: NativeEventType.FEATURE_LOADED,
                    parameters: { featureId },
                });

                this.registerLoaderUsage(featureId); // Register loader usage on the server
            }),

            this.loadStylesService.loadStyles(styles),
        ]);
    }

    private runOnlyCompleteStrategy() {
        const strategy = this._doc.readyState;

        if (strategy === LoadStrategy.Complete || this.platform.isServer) {
            this.start(strategy);
        }
    }

    private start(strategy: string) {
        const loaders = this.allLoaders.filter((l: LoaderLoadOptions) => l.options.strategy === strategy && l.state === LoadState.Created);
        for (const item of loaders) {
            item.state = LoadState.Scheduled;
        }
        if (loaders?.length > 0) {
            this.logger.info(`Scheduled ${loaders?.length} loader(s) with ${strategy} strategy.`);
        }

        this.evaluate(loaders);
    }

    private reset(strategies: string[]) {
        const loaders = this.allLoaders.filter((l: LoaderLoadOptions) => strategies.includes(l.options.strategy));

        for (const item of loaders) {
            item.state = LoadState.Created;
        }

        if (loaders?.length > 0) {
            this.logger.info(`${loaders?.length} loader(s) resetted after logout for strategies ${strategies.join('|')}.`);
        }
    }

    private startEventStrategy(event: EventContext<any>) {
        const loaders = this.allLoaders.filter(
            (l: LoaderLoadOptions) => l.options.strategy == LoadStrategy.Event && l.options.events?.[event.type]?.includes(event.name),
        );

        if (loaders?.length > 0) {
            this.logger.info(`Found ${loaders?.length} event handler(s) for event.`, loaders, event);
        }

        loaders.forEach((loader: LoaderLoadOptions) => this.load(loader, event));
    }

    private restart() {
        const scheduled = this.allLoaders.filter(
            (l: LoaderLoadOptions) =>
                l.state === LoadState.Scheduled &&
                ![LoadStrategy.JustLoggedIn.toString(), LoadStrategy.LoggedIn.toString()].includes(l.options.strategy),
        );
        this.evaluate(scheduled);
    }

    private initLoaders() {
        for (const ruleId of Object.keys(this.config.rules)) {
            const loaderId = this.config.rules[ruleId]?.id ?? ruleId;
            const options = this.config.rules[ruleId]!;

            // styles are a special case
            if (loaderId === this.STYLES_LOADER_ID) {
                this.loadStylesService.loadStyles(options.styles).then();
                continue;
            }

            const loaderFnConfig = this.registeredLoaderFns.find((l: LoaderFnConfig) => l.id === loaderId);

            if (!loaderFnConfig) {
                continue;
            }

            const strategy = this.platform.isServerRendered && this._usedLoaders().includes(loaderId) ? LoadStrategy.Preload : options.strategy;

            this.allLoaders.push({
                id: loaderId,
                loaderFn: loaderFnConfig.loaderFn,
                state: LoadState.Created,
                options: { ...options, strategy },
                additionalStylesFn: loaderFnConfig.additionalStylesFn,
                product: loaderFnConfig.product,
            });
        }
    }

    private evaluate(loaders: LoaderLoadOptions[]) {
        for (const loader of loaders) {
            this.dslService
                .evaluateExpression<boolean>(loader.options.enabled)
                .pipe(
                    take(1),
                    map((enabled) => enabled && this.checkLoaderEnabledForProduct(loader)),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe((enabled: boolean) => {
                    if (enabled && loader.state == LoadState.Scheduled) {
                        loader.state = LoadState.Enabled;
                        this.loadDispatcher$.next(loader);
                    }
                    this.eventsService.raise({ eventName: VanillaEventNames.FeatureEnabledStatus, data: { id: loader.id, enabled } });
                });
        }
    }

    private checkLoaderEnabledForProduct(loader: LoaderLoadOptions): boolean {
        return !this.productService.isSingleDomainApp || !loader.product || loader.product === this.productService.current.name;
    }

    private load(item: LoaderLoadOptions, eventContext?: EventContext<any>) {
        if (!this.checkLoaderEnabledForProduct(item)) {
            return;
        }

        try {
            const options: LoadOptions = {
                ...item.options,
            };

            if (eventContext) {
                options.eventContext = eventContext;
            }

            if (!options.delay) {
                this.runLoader(item, options);
            } else if (this.platform.isBrowser) {
                this.webWorkerService.createWorker(item.id, { timeout: options.delay }, () => {
                    this.runLoader(item, options);
                    this.webWorkerService.removeWorker(item.id);
                });
            }
        } catch (error) {
            this.logger.errorRemote(`Failed to load ${item.id} feature`, error);
        }

        this.logger.info('Loaded:', item);
    }

    private runLoader(item: LoaderLoadOptions, options: LoadOptions) {
        if (item.loaderFn) {
            const additionalStyles = this.getAdditionalStyles(item);
            this.loadFeature(item.id, item.loaderFn(this.getInjector()), options, additionalStyles);
        }

        item.state = LoadState.Loaded;
    }

    private getAdditionalStyles(item: LoaderLoadOptions): StyleOptions[] | undefined {
        if (!item.additionalStylesFn) {
            return undefined;
        }

        const injector = this.getInjector();

        return item.additionalStylesFn(injector);
    }

    private mergeStyles(optionStyles: StyleOptions[] | undefined, additionalStyles: StyleOptions[] | undefined): StyleOptions[] | undefined {
        if (optionStyles && !additionalStyles) {
            return optionStyles;
        }

        if (additionalStyles && !optionStyles) {
            return additionalStyles;
        }

        return optionStyles && additionalStyles ? [...optionStyles, ...additionalStyles] : undefined;
    }

    private getInjector(): Injector {
        return this.productService.current.injector;
    }

    private registerLoaderUsage(loaderId: string) {
        // we want this feature to only run on the server side
        if (this.platform.isBrowser) {
            return;
        }

        this._usedLoaders.update((usedLoaders) => {
            if (!usedLoaders.includes(loaderId)) {
                return [...usedLoaders, loaderId];
            }
            return usedLoaders;
        });
    }
}
